// U.S. state consumer privacy laws that grant residents rights over their
// personal data, rendered in the Privacy Policy. `notes` records where a
// state's law departs from the standard set of rights described above the
// table. Regulator links go to each office's consumer-protection home so they
// survive site reorganizations.

export interface StatePrivacyLaw {
  state: string
  law: string
  notes?: string
  regulatorName: string
  regulatorUrl: string
}

export const statePrivacyLaws: StatePrivacyLaw[] = [
  {
    state: 'California',
    law: 'California Consumer Privacy Act, as amended by the CPRA',
    notes:
      'Includes the right to know the categories of personal information collected, sold, or shared, and to limit use of sensitive personal information. No statutory appeal right, but our appeal process is available to you.',
    regulatorName: 'California Privacy Protection Agency',
    regulatorUrl: 'https://cppa.ca.gov',
  },
  {
    state: 'Colorado',
    law: 'Colorado Privacy Act',
    regulatorName: 'Colorado Attorney General',
    regulatorUrl: 'https://coag.gov',
  },
  {
    state: 'Connecticut',
    law: 'Connecticut Data Privacy Act',
    regulatorName: 'Connecticut Attorney General',
    regulatorUrl: 'https://portal.ct.gov/ag',
  },
  {
    state: 'Delaware',
    law: 'Delaware Personal Data Privacy Act',
    notes: 'Includes the right to obtain a list of the categories of third parties to which your personal data was disclosed.',
    regulatorName: 'Delaware Department of Justice',
    regulatorUrl: 'https://attorneygeneral.delaware.gov',
  },
  {
    state: 'Indiana',
    law: 'Indiana Consumer Data Protection Act',
    regulatorName: 'Indiana Attorney General',
    regulatorUrl: 'https://www.in.gov/attorneygeneral/',
  },
  {
    state: 'Iowa',
    law: 'Iowa Consumer Data Protection Act',
    notes: 'Does not include a right to correct or to opt out of profiling. Responses may take up to 90 days, extendable by 45.',
    regulatorName: 'Iowa Attorney General',
    regulatorUrl: 'https://www.iowaattorneygeneral.gov',
  },
  {
    state: 'Kentucky',
    law: 'Kentucky Consumer Data Protection Act',
    regulatorName: 'Kentucky Attorney General',
    regulatorUrl: 'https://www.ag.ky.gov',
  },
  {
    state: 'Maryland',
    law: 'Maryland Online Data Privacy Act',
    notes: 'Includes the right to obtain a list of the categories of third parties to which your personal data was disclosed.',
    regulatorName: 'Maryland Attorney General',
    regulatorUrl: 'https://www.marylandattorneygeneral.gov',
  },
  {
    state: 'Minnesota',
    law: 'Minnesota Consumer Data Privacy Act',
    notes:
      'Includes the right to obtain a list of specific third parties to which your personal data was disclosed, and to question the result of profiling that produced a significant decision about you.',
    regulatorName: 'Minnesota Attorney General',
    regulatorUrl: 'https://www.ag.state.mn.us',
  },
  {
    state: 'Montana',
    law: 'Montana Consumer Data Privacy Act',
    regulatorName: 'Montana Department of Justice, Office of Consumer Protection',
    regulatorUrl: 'https://dojmt.gov/consumer/',
  },
  {
    state: 'Nebraska',
    law: 'Nebraska Data Privacy Act',
    regulatorName: 'Nebraska Attorney General',
    regulatorUrl: 'https://ago.nebraska.gov',
  },
  {
    state: 'Nevada',
    law: 'Nevada Revised Statutes Chapter 603A',
    notes:
      'Nevada residents may opt out of the sale of covered information. Submit an opt-out request using the steps below.',
    regulatorName: 'Nevada Attorney General',
    regulatorUrl: 'https://ag.nv.gov',
  },
  {
    state: 'New Hampshire',
    law: 'New Hampshire Privacy Act',
    regulatorName: 'New Hampshire Attorney General',
    regulatorUrl: 'https://www.doj.nh.gov',
  },
  {
    state: 'New Jersey',
    law: 'New Jersey Data Privacy Act',
    regulatorName: 'New Jersey Division of Consumer Affairs',
    regulatorUrl: 'https://www.njconsumeraffairs.gov',
  },
  {
    state: 'Oregon',
    law: 'Oregon Consumer Privacy Act',
    notes: 'Includes the right to obtain a list of specific third parties to which your personal data was disclosed.',
    regulatorName: 'Oregon Department of Justice',
    regulatorUrl: 'https://www.doj.state.or.us/consumer-protection/',
  },
  {
    state: 'Rhode Island',
    law: 'Rhode Island Data Transparency and Privacy Protection Act',
    regulatorName: 'Rhode Island Attorney General',
    regulatorUrl: 'https://riag.ri.gov',
  },
  {
    state: 'Tennessee',
    law: 'Tennessee Information Protection Act',
    regulatorName: 'Tennessee Attorney General',
    regulatorUrl: 'https://www.tn.gov/attorneygeneral.html',
  },
  {
    state: 'Texas',
    law: 'Texas Data Privacy and Security Act',
    regulatorName: 'Texas Attorney General',
    regulatorUrl: 'https://www.texasattorneygeneral.gov/consumer-protection',
  },
  {
    state: 'Utah',
    law: 'Utah Consumer Privacy Act',
    notes: 'Does not include a right to correct, to opt out of profiling, or to appeal — our appeal process is still available to you.',
    regulatorName: 'Utah Division of Consumer Protection',
    regulatorUrl: 'https://dcp.utah.gov',
  },
  {
    state: 'Virginia',
    law: 'Virginia Consumer Data Protection Act',
    regulatorName: 'Virginia Attorney General',
    regulatorUrl: 'https://www.oag.state.va.us/consumer-protection',
  },
]

export interface PrivacyRight {
  name: string
  description: string
}

export const privacyRights: PrivacyRight[] = [
  {
    name: 'Access / Right to know',
    description:
      'Confirm whether we process your personal data and obtain a copy of it, including the categories of data collected, the sources, the purposes, and the categories of third parties we disclose it to.',
  },
  {
    name: 'Correction',
    description: 'Ask us to correct inaccurate personal data we hold about you.',
  },
  {
    name: 'Deletion',
    description: 'Ask us to delete personal data we collected from or about you, subject to legal exceptions.',
  },
  {
    name: 'Portability',
    description: 'Receive the personal data you provided to us in a portable, readily usable format.',
  },
  {
    name: 'Opt out of sale, sharing, and targeted advertising',
    description:
      'Stop us from selling your personal data, sharing it for cross-context behavioral advertising, or using it for targeted advertising.',
  },
  {
    name: 'Opt out of profiling',
    description:
      'Opt out of profiling in furtherance of decisions that produce legal or similarly significant effects. We do not engage in this type of profiling.',
  },
  {
    name: 'Appeal',
    description: 'Appeal our decision if we decline to act on your request.',
  },
  {
    name: 'Non-discrimination',
    description: 'Exercise any of these rights without being denied service, charged a different price, or receiving a different quality of service.',
  },
]

export type PrivacyRequestType = 'access' | 'delete' | 'correct' | 'portability' | 'opt-out' | 'appeal'

export interface PrivacyRequestOption {
  type: PrivacyRequestType
  label: string
  subject: string
}

export const privacyRequestOptions: PrivacyRequestOption[] = [
  { type: 'opt-out', label: 'Opt out of sale, sharing & targeted ads', subject: 'Privacy Request: Opt Out of Sale/Sharing/Targeted Advertising' },
  { type: 'access', label: 'Access my data', subject: 'Privacy Request: Access / Right to Know' },
  { type: 'delete', label: 'Delete my data', subject: 'Privacy Request: Deletion' },
  { type: 'correct', label: 'Correct my data', subject: 'Privacy Request: Correction' },
  { type: 'portability', label: 'Get a portable copy', subject: 'Privacy Request: Data Portability' },
  { type: 'appeal', label: 'Appeal a decision', subject: 'Privacy Request: Appeal' },
]

export const PRIVACY_CONTACT_EMAIL = 'contact@findonlinedegrees.org'
